import { useReducer, useRef, useState } from 'react';
import moment from 'moment-timezone';
import { getApi, postApi } from 'redux/apis';
import { wizardReducer, initialState } from 'components/EmployeeCost/DirectAllocationImport/wizardReducer';
import { loadFile } from 'components/EmployeeCost/SmartImport/parsers';
import { buildParsePayload } from 'components/EmployeeCost/SmartImport/buildParsePayload';
import {
	suggestColumnMappings,
	sampleValuesForColumn,
} from 'components/EmployeeCost/DirectAllocationImport/directAllocationMappingHelpers';
import {
	buildEmployeeGroups,
	buildImportPayload,
	parseBulkImportErrors,
	validatePercentSums,
	canImport,
	type PreValidationError,
	type RosterEmployee,
	type TreeOption,
} from 'components/EmployeeCost/DirectAllocationImport/directAllocationResolve';
import type { DirectAllocationResultSummary, PayPeriodOption } from './types';

const EMPLOYEES_URL = '/employees/list';
const CUSTOMER_OPTIONS_URL = '/quickbooks/customer-options';
const CLASS_OPTIONS_URL = '/quickbooks/class-options';
const BULK_IMPORT_URL = '/employee-config/bulk-import';
const PAY_PERIODS_URL = '/pay-periods';

const formatPeriodLabel = (p: { startDate?: string; endDate?: string }): string =>
	p.startDate && p.endDate
		? `${moment(p.startDate).tz(process.env.REACT_APP_TIMEZONE!).format('MM/DD/YYYY')} - ${moment(p.endDate)
				.tz(process.env.REACT_APP_TIMEZONE!)
				.format('MM/DD/YYYY')}`
		: '';

interface Params {
	companyId: string;
	appendMessage: (msg: { text: string; directAllocationStep?: import('./types').DirectAllocationStepKind }) => void;
	freezeActiveMessage: (text: string) => void;
	notifyImported: (payPeriodId: string) => void;
}

/**
 * Drives Employee Direct Allocation Import's own reducer/helpers
 * (components/EmployeeCost/DirectAllocationImport) as a sequence of chat cards
 * instead of a modal's Steps — mirrors SmartImportChat/useSmartImportChat.ts.
 * Roster/customer/class options are fetched directly here (not via the
 * getEmployeeAction/customerOptionsAction/classOptionsAction redux thunks the
 * modal's Step3Preview uses) so a chat-driven import never mutates the global
 * employees/customer/class redux slices from an arbitrary page.
 */
export function useDirectAllocationChat({ companyId, appendMessage, freezeActiveMessage, notifyImported }: Params) {
	const [session, dispatch] = useReducer(wizardReducer, initialState);

	const [payPeriodOptions, setPayPeriodOptions] = useState<PayPeriodOption[]>([]);
	const [payPeriodId, setPayPeriodId] = useState<string | null>(null);
	const [payPeriodLabel, setPayPeriodLabel] = useState('');
	const [roster, setRoster] = useState<RosterEmployee[]>([]);
	const [customerOptions, setCustomerOptions] = useState<TreeOption[]>([]);
	const [classOptions, setClassOptions] = useState<TreeOption[]>([]);
	const [loading, setLoading] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [result, setResult] = useState<DirectAllocationResultSummary | null>(null);
	// Bulk-import validation errors (422) stay on the SAME preview card, same
	// as the modal's Step3Preview shows its importErrors/generalError inline
	// rather than navigating away — there's no separate "fix mapping" step for
	// these codes, they're all about the employee/allocation rows edited here.
	const [importErrors, setImportErrors] = useState<PreValidationError[]>([]);
	const [generalError, setGeneralError] = useState<string | null>(null);

	// Parsed headers/rows survive across steps in a ref, same rationale as
	// useSmartImportChat's parsedRef — nothing here needs it to trigger a
	// re-render, and it must be read synchronously right after being written.
	const parsedRef = useRef<{ headers: string[] } | null>(null);
	const activeRef = useRef(false);

	const resetLocal = () => {
		setPayPeriodOptions([]);
		setPayPeriodId(null);
		setPayPeriodLabel('');
		setRoster([]);
		setCustomerOptions([]);
		setClassOptions([]);
		setLoading(false);
		setSubmitting(false);
		setResult(null);
		setImportErrors([]);
		setGeneralError(null);
		parsedRef.current = null;
	};

	// ---- Step: once a pay period is resolved, suggest the column mapping. -----
	const afterPayPeriodResolved = (resolvedPayPeriodId: string, label: string) => {
		setPayPeriodId(resolvedPayPeriodId);
		setPayPeriodLabel(label);
		const parsed = parsedRef.current;
		if (!parsed) return;

		const sampleValuesByHeader: Record<string, string[]> = {};
		parsed.headers.forEach((h) => {
			sampleValuesByHeader[h] = sampleValuesForColumn(session.rawRows, h);
		});
		const mappings = suggestColumnMappings(parsed.headers, sampleValuesByHeader);
		dispatch({ type: 'COLUMN_MAPPINGS_INITIALIZED', columnMappings: mappings });
		appendMessage({
			directAllocationStep: 'mapping',
			text: "Here's my best guess at the column mapping. Review and continue when ready.",
		});
	};

	/** Entry point: user attached a CSV/XLSX and chose Employee Direct Allocation. */
	const startImport = async (file: File) => {
		if (!companyId) {
			appendMessage({ text: 'No company is selected — pick a company before importing.' });
			return;
		}
		if (activeRef.current) {
			freezeActiveMessage('Cancelled — a new import was started.');
		}
		dispatch({ type: 'RESET' });
		resetLocal();
		activeRef.current = true;

		let detected;
		try {
			detected = await loadFile(file);
		} catch (e) {
			appendMessage({
				text: `I couldn't read that file${e instanceof Error ? `: ${e.message}` : ''}. Try a CSV or XLSX export.`,
			});
			activeRef.current = false;
			return;
		}

		dispatch({ type: 'FILE_SELECTED', file });
		const payload = buildParsePayload(detected);
		dispatch({ type: 'PARSE_SUCCEEDED', payload });
		dispatch({ type: 'ADVANCE_STEP' }); // past "upload" — the file is already attached

		const headers = (payload.rawMatrix[payload.headerRowIndex] ?? []).map(String);
		parsedRef.current = { headers };

		try {
			const res = await getApi(PAY_PERIODS_URL, { companyId });
			const list: Array<{ id: string; startDate?: string; endDate?: string }> = res.data?.data ?? [];
			if (list.length === 0) {
				appendMessage({ text: 'This company has no pay periods set up yet — create one before importing.' });
				return;
			}
			const storedId = localStorage.getItem('payPeriod');
			const stored = storedId ? list.find((p) => p.id === storedId) : undefined;
			if (stored) {
				afterPayPeriodResolved(stored.id, formatPeriodLabel(stored));
				return;
			}
			if (list.length === 1) {
				afterPayPeriodResolved(list[0].id, formatPeriodLabel(list[0]));
				return;
			}
			const options: PayPeriodOption[] = list.map((p) => ({ id: p.id, label: formatPeriodLabel(p) || p.id }));
			setPayPeriodOptions(options);
			appendMessage({ directAllocationStep: 'pay_period', text: 'Which pay period should this import into?' });
		} catch {
			appendMessage({ text: 'Could not load pay periods. Please try again.' });
		}
	};

	const selectPayPeriod = (id: string) => {
		const label = payPeriodOptions.find((p) => p.id === id)?.label ?? '';
		freezeActiveMessage(`Importing into ${label || 'the selected pay period'}.`);
		afterPayPeriodResolved(id, label);
	};

	// ---- Mapping step ------------------------------------------------------
	const hasEmployee = session.columnMappings.some((m) => m.target === 'employee');
	const hasPercent = session.columnMappings.some((m) => m.target === 'percent');
	const canAdvanceFromMapping = hasEmployee && hasPercent;

	const continueFromMapping = async () => {
		if (!canAdvanceFromMapping || !payPeriodId) return;
		freezeActiveMessage('Mapping confirmed.');
		setLoading(true);
		try {
			const [rosterRes, customerRes, classRes] = await Promise.all([
				postApi(EMPLOYEES_URL, { companyId, status: 'all' }, false, { limit: 10000, page: 1 }),
				postApi(CUSTOMER_OPTIONS_URL, { companyId }),
				postApi(CLASS_OPTIONS_URL, { companyId }),
			]);
			const rosterResult: RosterEmployee[] = rosterRes.data?.data?.data ?? [];
			const customerResult: TreeOption[] = customerRes.data?.data ?? [];
			const classResult: TreeOption[] = classRes.data?.data ?? [];
			setRoster(rosterResult);
			setCustomerOptions(customerResult);
			setClassOptions(classResult);

			const employeeGroups = buildEmployeeGroups(
				session.rawRows,
				session.columnMappings,
				rosterResult,
				customerResult,
				classResult
			);
			dispatch({ type: 'GROUPS_INITIALIZED', employeeGroups });
			dispatch({ type: 'ADVANCE_STEP' });

			const reviewCount = employeeGroups.filter((g) => g.matchStatus !== 'matched').length;
			appendMessage({
				directAllocationStep: 'preview',
				text:
					`Matched ${employeeGroups.length - reviewCount} of ${employeeGroups.length} employee(s) automatically.` +
					(reviewCount > 0 ? ` ${reviewCount} need your confirmation.` : '') +
					` Review below.`,
			});
		} catch {
			appendMessage({ text: 'Could not load employees, customers, and classes. Please try again.' });
		} finally {
			setLoading(false);
		}
	};

	// ---- Preview step ------------------------------------------------------
	const validations = validatePercentSums(session.employeeGroups);
	const ready = canImport(session.employeeGroups);

	const confirmImport = async () => {
		if (!payPeriodId || !ready) return;
		setSubmitting(true);
		setImportErrors([]);
		setGeneralError(null);
		try {
			const payload = buildImportPayload(session.employeeGroups, { companyId, payPeriodId });
			await postApi(BULK_IMPORT_URL, payload);
			const text = `Import complete: ${payload.rows.length} employee(s) updated for ${payPeriodLabel || 'the selected pay period'}.`;
			freezeActiveMessage(`Import confirmed for ${payPeriodLabel || 'the selected pay period'}.`);
			setResult({ success: true, text, errors: [] });
			appendMessage({ directAllocationStep: 'result', text });
			notifyImported(payPeriodId);
			activeRef.current = false;
		} catch (err: unknown) {
			const status = (err as { response?: { status?: number } })?.response?.status;
			if (status === 422) {
				setImportErrors(parseBulkImportErrors(err));
			} else {
				setGeneralError('Failed to import employee direct allocations. Please try again.');
			}
		} finally {
			setSubmitting(false);
		}
	};

	const cancelImport = () => {
		freezeActiveMessage('Import cancelled.');
		dispatch({ type: 'RESET' });
		resetLocal();
		activeRef.current = false;
	};

	return {
		session,
		dispatch,
		payPeriodOptions,
		payPeriodId,
		payPeriodLabel,
		roster,
		customerOptions,
		classOptions,
		loading,
		submitting,
		result,
		importErrors,
		generalError,
		canAdvanceFromMapping,
		validations,
		ready,
		startImport,
		selectPayPeriod,
		continueFromMapping,
		confirmImport,
		cancelImport,
	};
}

export type DirectAllocationChat = ReturnType<typeof useDirectAllocationChat>;
export type { PreValidationError };
